// The multipart splat's ONE frame slot — a driven pose or a recolour, never both at once —
// and the recolour request that must survive being asked while the slot is busy.
// Notes: ~/comments/aos-threejs-poc/splatFrameSlot_createFrameSlot_lost_recolour.md

/** How long the view must hold still before a driven frame re-runs appr: the FINAL stage of a
 *  move, not every frame of it — a moving character keeps its cached colour meanwhile. */
export const RECOLOUR_SETTLE_MS = 80
/** ...unless the colour has been stale this long: a long walk still refreshes on the way. */
export const RECOLOUR_MAX_STALE_MS = 300

/** One slot shared by the body drive and the appearance decode. */
export function createFrameSlot({ settleMs = RECOLOUR_SETTLE_MS, maxStaleMs = RECOLOUR_MAX_STALE_MS } = {}) {
  let inFlight = false
  let appearanceStale = false
  let driveOwned = false
  let lastMoveAt = -Infinity
  let lastDecodeAt = -Infinity

  return {
    get inFlight() { return inFlight },
    get appearanceStale() { return appearanceStale },
    get driveOwned() { return driveOwned },

    /** Take the slot for one frame; false while a frame is still presenting. */
    acquire() {
      if (inFlight) return false
      inFlight = true
      return true
    },
    release() { inFlight = false },

    /** The view moved at `now`. True when the caller must recolour itself — no drive will fold it in. */
    requestAppearance(now = 0) {
      appearanceStale = true
      lastMoveAt = now
      return !driveOwned
    },

    /** The frame holding the slot decodes appearance NOW: clears the request and reports it. */
    consumeAppearance(now = 0) {
      const wanted = appearanceStale
      appearanceStale = false
      lastDecodeAt = now // the caller decodes whether or not a request was pending
      return wanted
    },

    /** A DRIVEN frame asks: decode only once the view has settled, or the colour is overdue. */
    consumeSettledAppearance(now) {
      if (!appearanceStale) return false
      if (now - lastMoveAt < settleMs && now - lastDecodeAt < maxStaleMs) return false
      return this.consumeAppearance(now)
    },

    /** The drive reports each frame whether it paces the rig; `false` hands recolours back. */
    setDriveOwned(owned) { driveOwned = !!owned },
  }
}
